import labels, { fileUploadErrors } from '../data/labels';
import EnvConfigHelper from '../helpers/envConfigHelper';
import EventHandler from './EventHandler';
import ServiceBackendEventsHelper from './helpers/ServiceBackendEventsHelper';
import { limitSubjectLength } from '../helpers/chatHelpers';
import { HTTPDataService, HttpError } from './HTTPDataService';
import { ChatMessageTracesResponse, ChatMessageStream, ChatMetadata, GetChatResponse, SessionResponse, DocumentInfoModel, DocumentUpdateRequest, DocumentsResponse, DeleteDocumentsResponse, DocumentArtifactsUrlRequest, SessionsResponse } from '../models/ChatTypes';
import { ChatMessageFeedback, ChatMessageFeedbackResponse, DocumentProcessingMode, ProductivityGainResponse, PromptPayload, UpdateChatMetadataResponse, UserPreferences, Vote } from '../models/types';
import { IChatService } from './IChatService';
import { IBackendEventsService } from './IBackendEventsService';
import { ActionRequest, ActionResult, ComponentsData, DataRequest, FormAction, TableRowsResponse } from '../models/SidePanelTypes';
import { DocumentEvent, SessionEvent } from '../models/BackendEvents';
import { AxiosProgressEvent } from 'axios';
import { dictionaryToQueryString } from '../helpers/routeHelpers';
import { createLocalMessageId } from '../helpers/messageIdHelpers';
import { advancedAssistantKey } from '../constants/consts';
import TaskPromise from './TaskPromise';

const getResponseContent = async (response: Response) => {
    const text = await response.text();
    const result = { text, data: undefined as any };
    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/json'))
        try {
            result.data = JSON.parse(text);
        }
        catch (e) {
            console.error('Unable to parse json response', e);
        }
    return result;
};

class ChatService extends HTTPDataService implements IChatService {
    baseURL = EnvConfigHelper.get('api-base-url', '');
    backendEventsHelper: ServiceBackendEventsHelper;
    constructor(public getAccessToken: () => Promise<string | undefined>, backendEvents: IBackendEventsService) {
        super();
        this.backendEventsHelper = new ServiceBackendEventsHelper(this.events, backendEvents);
    }

    getChat = async (sessionId: string) => {
        const session = await this.get<GetChatResponse>(`v1/plus1-chatbot/my-session/${sessionId}`, 
            { 'include-events': 'true', 'include-documents': 'true' }
        );
        if (!session.session.persona)
            session.session.persona = advancedAssistantKey;
        return session;
    };

    getAllChats = async () => {
        const sessionResponse = await this.getObjectArray<SessionsResponse, SessionResponse>('v1/plus1-chatbot/my-sessions', 'sessions');
        return sessionResponse;
    };

    createChat = async (initialMessage: string, llm: string, temperature: number, persona?: string, version?: string, build?: number, documents?: string[], data?: Record<string, any>) => {
        const trimmedMessage = limitSubjectLength(initialMessage);
        // Advanced persona is not known to the backend. When advanced persona is used, only the model and temperature should be submitted
        persona = persona !== advancedAssistantKey ? persona : undefined;
        if (!persona) {
            version = build = undefined;
        }
        llm = "dps-plus1-model";
        const result = await this.post<SessionResponse>('v1/plus1-chatbot/create-session', {}, {
            llm,
            temperature,
            subject: trimmedMessage,
            persona,
            version,
            build,
            documents,
            data
        });
        return result;
    };

    updateChatMetadata = async (sessionId: string, metadata: Partial<ChatMetadata>,) => {
        const result = await this.patch<UpdateChatMetadataResponse>(`v1/plus1-chatbot/my-session/${sessionId}`, {}, metadata);
        return result;
    };

    setSessionCurrentPersonaVersion = async (sessionId: string, version: string, build: number) => {
        const uri = `v1/plus1-chatbot/my-sessions/${sessionId}/session-current-persona-version`;
        const payload = { version, build };
        const result = await this.patch<SessionResponse>(uri, {}, payload);
        return result;
    };

    uploadDocument = async ({ file, sessionId, onUploadProgress, personaId, processingMode }: { file: File, sessionId?: string, onUploadProgress?: (progressEvent: AxiosProgressEvent) => void, personaId?: string; processingMode?: DocumentProcessingMode; }) => {
        const formData = new FormData();
        formData.append("file", file);
        let query = {} as Record<string, string>;
        if (processingMode) {
            query["processing_mode"] = processingMode;
        }

        if (sessionId && sessionId.trim() !== "") {
            query["session_id"] = sessionId;
        }
        else if (personaId && personaId.trim() !== "") {
            query["persona_id"] = personaId;
        }

        const result = await this.post<DocumentInfoModel>('api/me/document', query, formData, onUploadProgress);
        return result;
    };

    copyDocuments = async ({ document_ids }: { document_ids: string[]; }): Promise<DocumentsResponse> => {
        const requestBody = { document_ids };

        try {
            const response: DocumentsResponse = await this.post<DocumentsResponse>('api/me/document/copy', undefined, requestBody);
            return response;
        }
        catch (error) {
            console.error(fileUploadErrors.copyError.headline, error);
            return { documents: {}, errors: { general: fileUploadErrors.copyError.headline } };
        }
    };

    fetchDocuments = async ({ fileIds, sessionId }: { fileIds: string[], sessionId?: string; }): Promise<DocumentsResponse> => {
        if (fileIds.length === 0 && !sessionId) {
            console.warn(labels.noLocalDocumentsMessage);
            return { documents: {} };
        }

        try {
            const response: DocumentsResponse = await this.getDocuments({ fileIds, sessionId });
            return response;
        }
        catch (error) {
            console.error(labels.fetchErrorMessage, error);
            return { documents: {}, errors: { general: labels.fetchErrorMessage } };
        }
    };

    downloadDocuments = async ({ fileIds }: { fileIds: string[]; }): Promise<boolean> => {
        let allSuccess = true;
        for (const fileId of fileIds) {
            const success = await this.download(`api/me/document/${fileId}/artifacts/raw`);

            if (!success) {
                console.error(`${labels.downloadErrorUUID} ${fileId}`);
                allSuccess = false;
            }
        }
        return allSuccess;
    };

    getDocumentArtifactUrl = async ({ documentId, ttl, artifact, contentDisposition }: DocumentArtifactsUrlRequest): Promise<string> => {
        const uri = `api/me/document/${documentId}/artifacts/${artifact}/sas`;
        const query = { ttl_minutes: ttl.toString(), content_disposition: contentDisposition };

        const result = await this.get<string>(uri, query);
        await TaskPromise.delay(500); // Wait for 0.5 seconds to ensure the URL is valid
        return result;
    };

    getDocuments = async ({ fileIds, sessionId, personaId }: { fileIds?: string[], sessionId?: string, personaId?: string; }): Promise<DocumentsResponse> => {
        const queryParams = dictionaryToQueryString({
            dict: {
                ...(personaId && !fileIds?.length && !sessionId ? { persona_id: personaId } : {}),
                ...(sessionId && !fileIds?.length && !personaId ? { session_id: sessionId } : {}),
                ...(fileIds && !sessionId && !personaId ? { document_ids: fileIds.join(',') } : {})
            }
        });

        const fullQuery = queryParams;
        const result = await this.get<DocumentsResponse>(`api/me/document${fullQuery}`);

        return result || { documents: {} };
    };

    updateDocumentMetadata = async ({ fileId, metadata }: { fileId: string, metadata: DocumentUpdateRequest; }) => {
        const uri = `api/me/document/${fileId}`;

        const body = {
            display: {
                name: metadata.display.name,
                description: metadata.display.description || null
            }
        };

        const result = await this.patch<DocumentInfoModel>(uri, undefined, body);
        return result;
    };

    deleteDocuments = async ({ fileIds }: { fileIds: string[]; }): Promise<DeleteDocumentsResponse> => {
        const uri = `api/me/document`;

        const body = { document_ids: fileIds };

        const result = await this.delete<DeleteDocumentsResponse>(uri, undefined, body);

        return result;
    };

    deleteChat = (sessionId: string) => this.delete<void>(`v1/plus1-chatbot/my-session/${sessionId}`);
    deleteAllChats = () => this.delete<void>(`v1/plus1-chatbot/delete-all`);

    submitProductivityGain = async (sessionId: string, productivityGain: number) => {
        const uri = `v1/plus1-chatbot/my-session/${sessionId}/productivity-gain`;
        const query = { session_id: sessionId };
        const payload = { productivity_gain: productivityGain };
        return this.patch<ProductivityGainResponse>(uri, query, payload);
    };

    prompt = (message: string, sessionId: string, promptTemplateIds?: string) => new Promise<ChatMessageStream>(async (resolve, reject) => {
        const payload: PromptPayload = { message: message, prompt_template_id: promptTemplateIds };


        const uri = this.toAbsoluteApiUrl(`v1/plus1-chatbot/my-session/${sessionId}`);
        const aborter = new AbortController();
        const token = this.getAccessToken();

        try { await token; }
        catch (e) { return reject(e); }

        const promise = fetch(uri, {
            method: 'POST',
            signal: aborter.signal,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${await token}`
            },
            body: JSON.stringify(payload),
        });

        let result: ChatMessageStream | undefined;
        let error: HttpError | undefined;
        try {
            const response = await promise;

            if (!response.ok) {
                if (response.status === 401 || response.status === 403) {
                    HTTPDataService.triggerUnauthorized();
                }

                error = {
                    status: response.status,
                    statusText: response.statusText,
                    ...await getResponseContent(response)
                };
            }
            else {
                const aiId = response.headers.get('x-ai-message-id') ?? createLocalMessageId();
                const humanId = response.headers.get('x-human-message-id') ?? createLocalMessageId();
                
                result = {
                        response,
                        aiId,
                        humanId,
                        abort: () => aborter.abort(),
                    };
            }
        }
        catch (e) {
            // https://developer.mozilla.org/en-US/docs/Web/API/fetch#exceptions
            if (e instanceof DOMException) error = { status: 0, statusText: 'abort' };
            else error = { status: 0, statusText: 'network_error' };
        }

        if (result) resolve(result);
        else reject(error ?? { status: -1, statusText: '' });
    });

    private _messageVote = async (sessionId: string, message_id: string, vote: Vote) => {
        const uri = `v1/plus1-chatbot/my-session/${sessionId}/vote`;
        const payload = { vote, message_id };
        const result = await this.patch<void>(uri, undefined, payload);
        return result;
    };

    messageLike = async (sessionId: string, messageId: string) => {
        const result = await this._messageVote(sessionId, messageId, 1);
        return result;
    };
    messageDislike = async (sessionId: string, messageId: string) => {
        const result = await this._messageVote(sessionId, messageId, -1);
        return result;
    };
    messageResetVote = async (sessionId: string, messageId: string) => {
        const result = await this._messageVote(sessionId, messageId, 0);
        return result;
    };

    messageComment = async (sessionId: string, message_id: string, comment: string) => {
        const uri = `v1/plus1-chatbot/my-session/${sessionId}/comment`;
        const payload = { comment, message_id };
        const result = await this.patch<void>(uri, undefined, payload);
        return result;
    };

    messageInsights = async (traceId: string) => {
        const uri = `v1/plus1-chatbot/my-session/trace/${traceId}`;
        const result = await this.get<ChatMessageTracesResponse>(uri);
        return result;
    };

    setUserPreferences = async (listPatchMode: 'replace' | 'add-items' | 'remove-items', preferences: Partial<UserPreferences>) => {
        const uri = 'api/me/preferences';
        const query = { 'list-patch-mode': listPatchMode };
        return await this.patch<UserPreferences>(uri, query, preferences);
    };

    getUserPreferences = async () => {
        const uri = 'api/me/preferences';
        const preferences = await this.get<UserPreferences>(uri);
        return preferences;
    };

    tableData = async (sessionId: string, source: string, page?: number, params?: Record<string, string[]>): Promise<TableRowsResponse> => {
        const uri = `v1/plus1-chatbot/my-session/${sessionId}/side-panel-data`;
        const query: DataRequest = { session_id: sessionId, data_key: source };
        const result = await this.get<TableRowsResponse>(uri, query);
        return result ?? { rows: [], count: 0 };
    };

    dataSidePanel = async (sessionId: string, objectId: string) => {
        const uri = `v1/plus1-chatbot/my-session/${sessionId}/side-panel-data`;
        const query: DataRequest = {
            session_id: sessionId,
            data_key: decodeURIComponent(objectId)
        };
        const response = await this.get<{ data: ComponentsData; }>(uri, query);
        return response.data;
    };
    dataSidePanelAction = async <F extends FormAction>(sessionId: string, formAction: F, data?: Record<string, any>): Promise<ActionResult<F>> => {
        const uri = `v1/plus1-chatbot/my-session/${sessionId}/side-panel-data`;
        const query: ActionRequest = {
            session_id: sessionId,
            data_key: decodeURIComponent(formAction.data_key),
            action: formAction.name,
        };
        const action = formAction.action;
        switch (action.type) {
            case "download": {
                const response = await this.download(uri, action.content_type, action.file_name, "POST", query, data, true);
                return response as any;
            }
            case "submit":
            default:
                {
                    const response = await this.post<{ data: ComponentsData; }>(uri, query, data);
                    return response.data as any;
                }
        }
    };

    private _getMessagesFeedback = async (sessionId: string, message_id?: string) => {
        const uri = `v1/plus1-chatbot/my-session/${sessionId}/feedback`;
        const params: { message_id?: string; } = {};
        if (message_id) params.message_id = message_id;
        const result = await this.getObjectArray<ChatMessageFeedbackResponse, ChatMessageFeedback>(uri, 'feedback', params);

        return result;
    };

    getChatMessagesFeedback = async (sessionId: string) => {
        const result = await this._getMessagesFeedback(sessionId);
        return result;
    };
    getMessageFeedback = async (sessionId: string, messageId: string) => {
        const [result] = await this._getMessagesFeedback(sessionId, messageId);
        return result;
    };

    events = {
        session: new EventHandler<SessionEvent>(),
        document: new EventHandler<DocumentEvent>()
    };

    bindEvents = () => {
        this.backendEventsHelper.bind();
    };
    unbindEvents = () => {
        this.backendEventsHelper.unbind();
    };
}

export default ChatService;
